import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import type { Channel } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { EMPTY_TYPING, presenceOf, useChatStore, type ChannelSummary } from '../chat/chatStore.js';
import { useDirectory } from '../chat/directoryStore.js';
import { counterpartId, formatListTime, previewText, typingLabel } from '../chat/labels.js';
import { Avatar } from './Avatar.js';
import { DeleteChatDialog } from './DeleteChatDialog.js';
import { MessageMenu } from './MessageMenu.js';
import { EmptyState } from './EmptyState.js';
import { Icon } from './Icon.js';
import { Skeleton } from './Skeleton.js';

/**
 * The conversation rail: filter, then direct messages and groups.
 *
 * Rows are ordered by last activity rather than creation date, so a channel that
 * just received a message rises to the top - which is the order people actually
 * look for a conversation in.
 */
export function ChannelList({ onNavigate }: { onNavigate?: () => void }) {
  const channels = useChatStore((state) => state.channels);
  const status = useChatStore((state) => state.channelsStatus);
  const error = useChatStore((state) => state.channelsError);
  const summaries = useChatStore((state) => state.summaries);
  const loadChannels = useChatStore((state) => state.loadChannels);
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);

  const [filter, setFilter] = useState('');

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  // One lookup for every counterpart on the rail, not one per row: a direct
  // channel's name may be a placeholder until the directory resolves.
  const memberIds = useMemo(
    () =>
      [
        ...new Set(
          channels.flatMap((channel) =>
            channel.type === 'direct' ? channel.memberIds.filter((id) => id !== currentUserId) : [],
          ),
        ),
      ].sort(),
    [channels, currentUserId],
  );
  const directory = useDirectory(memberIds);

  const ordered = useMemo(() => {
    const activityOf = (channel: Channel): string => summaries[channel.id]?.at ?? channel.createdAt;

    return [...channels].sort((a, b) => activityOf(b).localeCompare(activityOf(a)));
  }, [channels, summaries]);

  const needle = filter.trim().toLowerCase();
  const matches = needle
    ? ordered.filter((channel) =>
        channelLabel(channel, currentUserId, directory).toLowerCase().includes(needle),
      )
    : ordered;

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="rail-scroll" aria-busy="true" aria-label="Loading conversations">
        {[0, 1, 2, 3, 4].map((row) => (
          <div className="channel-skeleton" key={row}>
            <Skeleton width={36} height={36} circle />
            <div className="channel-skeleton-body">
              <Skeleton width={`${60 - row * 6}%`} height={11} />
              <Skeleton width={`${85 - row * 8}%`} height={9} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="rail-scroll">
        <div className="rail-error">
          <p className="field-error">
            <Icon name="alertCircle" size={13} />
            {error}
          </p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => void loadChannels()}
          >
            <Icon name="rotate" size={13} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (channels.every((channel) => channel.type === 'saved') && !filter) {
    return (
      <div className="rail-scroll">
        <ul className="channel-list channel-list--pinned">
          {channels.map((channel) => (
            <ChannelRow key={channel.id} channel={channel} onNavigate={onNavigate} />
          ))}
        </ul>
        <EmptyState
          compact
          icon="messagesEmpty"
          title="No conversations yet"
          description="Start one with “New conversation” above to message a colleague or open a group channel."
        />
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="rail-scroll">
        <EmptyState
          compact
          icon="messagesEmpty"
          title="No conversations yet"
          description="Start one with “New conversation” above to message a colleague or open a group channel."
        />
      </div>
    );
  }

  const saved = matches.filter((channel) => channel.type === 'saved');
  const direct = matches.filter((channel) => channel.type === 'direct');
  const groups = matches.filter((channel) => channel.type === 'group');

  return (
    <>
      <div className="rail-top">
        <div className="input-group">
          <Icon name="search" size={15} />
          <input
            className="input"
            type="search"
            value={filter}
            placeholder="Filter conversations"
            aria-label="Filter conversations"
            onChange={(event) => setFilter(event.target.value)}
          />
          {filter ? (
            <button
              type="button"
              className="btn btn--icon btn--sm input-clear"
              aria-label="Clear filter"
              onClick={() => setFilter('')}
            >
              <Icon name="x" size={13} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="rail-scroll">
        {matches.length === 0 ? (
          <EmptyState
            compact
            icon="search"
            title="No matches"
            description={`Nothing here is called “${filter.trim()}”.`}
          />
        ) : null}

        {saved.length > 0 ? (
          // Pinned above everything, as a messenger pins "Saved messages".
          <ul className="channel-list channel-list--pinned">
            {saved.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} onNavigate={onNavigate} />
            ))}
          </ul>
        ) : null}
        {direct.length > 0 ? (
          <ChannelGroup heading="Direct messages" channels={direct} onNavigate={onNavigate} />
        ) : null}
        {groups.length > 0 ? (
          <ChannelGroup heading="Channels" channels={groups} onNavigate={onNavigate} />
        ) : null}
      </div>
    </>
  );
}

function ChannelGroup({
  heading,
  channels,
  onNavigate,
}: {
  heading: string;
  channels: Channel[];
  onNavigate?: (() => void) | undefined;
}) {
  return (
    <section>
      <div className="rail-heading">
        <span className="overline">{heading}</span>
        <span className="rail-count">{channels.length}</span>
      </div>
      <ul className="channel-list">
        {channels.map((channel) => (
          <ChannelRow key={channel.id} channel={channel} onNavigate={onNavigate} />
        ))}
      </ul>
    </section>
  );
}

function ChannelRow({
  channel,
  onNavigate,
}: {
  channel: Channel;
  onNavigate?: (() => void) | undefined;
}) {
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const presence = useChatStore((state) => state.presence);
  const summary = useChatStore((state) => state.summaries[channel.id]);
  const unread = useChatStore((state) => state.unread[channel.id] ?? 0);
  const unconfirmed = useChatStore((state) => state.pending[channel.id]?.length ?? 0);
  const typing = useChatStore((state) => state.typing[channel.id] ?? EMPTY_TYPING);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const other = counterpartId(channel, currentUserId);
  const typers = Object.keys(typing);
  const directory = useDirectory(other ? [other, ...typers] : typers.length ? typers : EMPTY_IDS);

  const name = channelLabel(channel, currentUserId, directory);
  const isDirect = channel.type === 'direct';
  const isSaved = channel.type === 'saved';
  const typingNow = typingLabel(typing, currentUserId, directory, isDirect);

  const classes = ['channel-row'];

  if (unread > 0) {
    classes.push('channel-row--unread');
  }

  return (
    <li>
      <NavLink
        to={`/channels/${channel.id}`}
        onClick={onNavigate}
        className={({ isActive }) =>
          isActive ? `${classes.join(' ')} channel-row--active` : classes.join(' ')
        }
        onContextMenu={(event) => {
          // Shift + right-click keeps the browser's own menu, as on messages.
          if (event.shiftKey) {
            return;
          }

          event.preventDefault();
          setMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        <Avatar
          name={name}
          id={other ?? channel.id}
          group={channel.type === 'group'}
          saved={isSaved}
          presence={isDirect && other ? presenceOf(presence, other) : undefined}
        />

        <span className="channel-body">
          <span className="channel-line">
            <span className="channel-name">{name}</span>
            {summary ? <span className="channel-time">{formatListTime(summary.at)}</span> : null}
          </span>

          <span className="channel-line">
            {typingNow ? (
              <span className="channel-preview channel-preview--typing">{typingNow}</span>
            ) : (
              <span className="channel-preview" dir="auto">
                {previewFor(summary, currentUserId, channel)}
              </span>
            )}

            <span className="channel-trail">
              {unconfirmed > 0 ? (
                <span className="channel-sending" title="Sending…" aria-label="Sending" />
              ) : null}
              {unread > 0 ? (
                <span className="badge" aria-label={`${unread} unread`}>
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : channel.type === 'group' ? (
                <span className="channel-members" title={`${channel.memberIds.length} members`}>
                  <Icon name="users" size={11} />
                  {channel.memberIds.length}
                </span>
              ) : null}
            </span>
          </span>
        </span>
      </NavLink>

      {menu ? (
        <MessageMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          actions={[
            isSaved
              ? {
                  id: 'clear',
                  label: 'Clear saved messages',
                  icon: 'eraser',
                  danger: true,
                  onSelect: () => setDeleting(true),
                }
              : isDirect
                ? {
                    id: 'delete',
                    label: 'Delete chat',
                    icon: 'trash',
                    danger: true,
                    onSelect: () => setDeleting(true),
                  }
                : {
                    id: 'leave',
                    label: 'Leave group',
                    icon: 'logOut',
                    danger: true,
                    onSelect: () => setDeleting(true),
                  },
          ]}
        />
      ) : null}

      {deleting ? (
        <DeleteChatDialog channel={channel} title={name} onClose={() => setDeleting(false)} />
      ) : null}
    </li>
  );
}

/** Stable empty list, for a group row that has no counterpart to resolve. */
const EMPTY_IDS: string[] = [];

/**
 * A direct channel's server-side name is not the other person's name, so the
 * rail resolves it from the directory and falls back to the stored name.
 */
function channelLabel(
  channel: Channel,
  currentUserId: string | null,
  directory: Record<string, { displayName: string }>,
): string {
  if (channel.type !== 'direct') {
    return channel.name;
  }

  const other = counterpartId(channel, currentUserId);

  return other ? (directory[other]?.displayName ?? channel.name) : channel.name;
}

function previewFor(
  summary: ChannelSummary | undefined,
  currentUserId: string | null,
  channel: Channel,
): string {
  if (!summary) {
    return channel.type === 'saved'
      ? 'Notes, kept messages and files for your devices'
      : channel.type === 'direct'
        ? 'No messages yet'
        : 'Channel created';
  }

  const mine =
    channel.type !== 'saved' && currentUserId !== null && summary.senderId === currentUserId;
  const body = previewText(summary.content);

  const text = summary.type === 'file-share-event' ? `📎 ${body}` : body;

  return mine ? `You: ${text}` : text;
}
